import { fmtMoney, useBudget } from "../api";
import { ErrorBox, Loading } from "../components/State";

export default function Budget() {
  const { data: b, loading, error } = useBudget();
  if (loading) return <Loading what="budget" />;
  if (error) return <ErrorBox error={error} what="budget" />;
  if (!b) return <ErrorBox error="No active budget" what="budget" />;

  const revenue = b.lines.filter((l) => l.isRevenue);
  const costs = b.lines.filter((l) => !l.isRevenue);
  const over = costs.filter((l) => l.variance > 0);

  return (
    <>
      <div className="card">
        <h2>{b.name}</h2>
        <p className="mono-inline">
          {b.periodStart} → {b.periodEnd} · {b.monthsInPeriod} months · {b.currency}
        </p>
        <div className="grid-2">
          <div className="stat">
            <strong className={b.runwayStatus === "critical" ? "over" : "ok"}>
              {b.runwayMonths === null ? "generative" : `${b.runwayMonths.toFixed(1)} mo`}
            </strong>
            <span>Runway — {b.runwayStatus}</span>
          </div>
          <div className="stat">
            <strong>{fmtMoney(b.openingCash, b.currency)}</strong>
            <span>Opening cash</span>
          </div>
          <div className="stat">
            <strong>{fmtMoney(b.closingCash, b.currency)}</strong>
            <span>Closing cash</span>
          </div>
          <div className="stat">
            <strong>{fmtMoney(Math.round(b.monthlyBurn), b.currency)}</strong>
            <span>Monthly net burn</span>
          </div>
        </div>
      </div>

      {b.runwayStatus === "critical" && (
        <div className="card">
          <span className="badge">Runway warning</span>
          <p style={{ marginTop: 10 }}>
            Under three months of cash at the current burn. Either close funding or cut{" "}
            {fmtMoney(Math.round(b.monthlyBurn * 3 - b.closingCash), b.currency)} of spend to reach a
            three-month floor.
          </p>
        </div>
      )}

      <div className="card">
        <h2>Revenue</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Planned</th>
              <th>Actual</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {revenue.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.categoryLabel}
                  {l.notes && <div>{l.notes}</div>}
                </td>
                <td className="mono-inline">{fmtMoney(l.planned, b.currency)}</td>
                <td className="mono-inline">{fmtMoney(l.actual, b.currency)}</td>
                <td className={`mono ${l.variance >= 0 ? "ok" : "over"}`}>
                  {l.variance >= 0 ? "+" : ""}
                  {fmtMoney(l.variance, b.currency)}
                </td>
              </tr>
            ))}
            <tr>
              <td><strong>Total revenue</strong></td>
              <td className="mono-inline">{fmtMoney(b.plannedRevenue, b.currency)}</td>
              <td className="mono-inline"><strong>{fmtMoney(b.actualRevenue, b.currency)}</strong></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Costs</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Planned</th>
              <th>Actual</th>
              <th>Variance</th>
            </tr>
          </thead>
          <tbody>
            {costs.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.categoryLabel}
                  {l.notes && <div>{l.notes}</div>}
                </td>
                <td className="mono-inline">{fmtMoney(l.planned, b.currency)}</td>
                <td className="mono-inline">{fmtMoney(l.actual, b.currency)}</td>
                <td className={`mono ${l.variance > 0 ? "over" : "ok"}`}>
                  {l.variance >= 0 ? "+" : ""}
                  {fmtMoney(l.variance, b.currency)}
                </td>
              </tr>
            ))}
            <tr>
              <td><strong>Total cost</strong></td>
              <td className="mono-inline">{fmtMoney(b.plannedCost, b.currency)}</td>
              <td className="mono-inline"><strong>{fmtMoney(b.actualCost, b.currency)}</strong></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {over.length > 0 && (
        <div className="card">
          <h2>Over plan</h2>
          <ul>
            {over.map((l) => (
              <li key={l.id}>
                <strong>{l.categoryLabel}</strong> — {fmtMoney(l.variance, b.currency)} over
                {l.notes ? ` · ${l.notes}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
